<!--
+------------------------------------------------------------------+
| Author: Jeffrey Thümann                                          |
| Component: StatCard                                              |
| Section: atoms                                                   |
| Purpose: Animated stat card for headline numeric counters.       |
+------------------------------------------------------------------+
-->
<template>
    <a :href="href || 'javascript:void(0);'" :class="{ clickable: href }">
        <!-- Ensuring visibility only when animatedValue >= 1 -->
        <div class="stat-value">
            <h2 :class="{ visible: isAnimating && animatedValue >= 1 }">{{ formattedAnimatedValue }}</h2>
            <span v-if="showChangeMarker" :class="['change-marker', changeDirectionClass]" :aria-label="changeAriaLabel"
                tabindex="0">
                {{ formattedChange }}
                <span class="change-tooltip" role="tooltip">{{ $t('home.statCards.changeTooltip') }}</span>
            </span>
        </div>
        <p class="eyebrow exception">{{ label }}</p>
    </a>
</template>

<script>
export default {
    name: 'StatCard',
    props: {
        label: { type: String, required: true },
        value: { type: [String, Number], required: true },
        href: { type: String, default: null },
        maxValue: { type: Number, required: true },
        change: { type: [String, Number], default: 0 },
        baseDuration: { type: Number, default: 1000 }, // Fixed base duration
        delay: { type: Number, default: 0 }, // Delay before animation starts
        animateOnMount: { type: Boolean, default: true }
    },
    data() {
        return {
            animatedValue: 0, // Starts from 0
            isAnimating: false, // Controls animation visibility
            hasValueAnimationFinished: false,
            animationTimeout: null,
            animationFrame: null
        };
    },
    computed: {
        formattedAnimatedValue() {
            if (this.animatedValue === null || this.animatedValue === undefined) return '';
            return Number(this.animatedValue).toLocaleString('nl-NL');
        },
        numericChange() {
            const value = Number(this.change);
            return Number.isFinite(value) ? value : 0;
        },
        showChangeMarker() {
            return this.numericChange !== 0 && this.hasValueAnimationFinished;
        },
        isPositiveChange() {
            return this.numericChange > 0;
        },
        changeDirectionClass() {
            return this.isPositiveChange ? 'change-marker--up' : 'change-marker--down';
        },
        formattedChange() {
            const prefix = this.isPositiveChange ? '+' : '';
            return `${prefix}${this.numericChange.toLocaleString('nl-NL')}`;
        },
        changeAriaLabel() {
            return `${this.$t('home.statCards.changeTooltip')}: ${this.formattedChange}`;
        }
    },
    watch: {
        value: {
            immediate: true,
            handler(newValue) {
                this.syncValueAnimation(newValue);
            }
        },
        animateOnMount() {
            this.syncValueAnimation(this.value);
        }
    },
    beforeUnmount() {
        this.clearValueAnimation();
    },
    methods: {
        syncValueAnimation(newValue) {
            const parsedValue = parseInt(newValue, 10);

            this.clearValueAnimation();
            this.hasValueAnimationFinished = false;

            if (!Number.isFinite(parsedValue) || parsedValue === 0) {
                // Keep hidden if value is 0
                this.isAnimating = false;
                this.animatedValue = 0;
                this.hasValueAnimationFinished = false;
                return;
            }

            if (!this.animateOnMount) {
                this.animatedValue = parsedValue;
                this.isAnimating = true;
                this.hasValueAnimationFinished = true;
                return;
            }

            this.isAnimating = false;
            this.animatedValue = 0;

            const duration = this.baseDuration;
            this.animationTimeout = setTimeout(() => {
                this.animationTimeout = null;
                this.animateValue(0, parsedValue, duration);
            }, this.delay);
        },
        clearValueAnimation() {
            if (this.animationTimeout) {
                clearTimeout(this.animationTimeout);
                this.animationTimeout = null;
            }

            if (this.animationFrame) {
                cancelAnimationFrame(this.animationFrame);
                this.animationFrame = null;
            }
        },
        animateValue(start, end, duration) {
            const range = end - start;
            let startTime = null;

            const animate = (timestamp) => {
                if (!startTime) startTime = timestamp;
                const progress = timestamp - startTime;
                const percentage = Math.min(progress / duration, 1);
                this.animatedValue = Math.floor(percentage * range + start);

                // Only trigger isAnimating when animatedValue is at least 1
                if (!this.isAnimating && this.animatedValue >= 1) {
                    this.isAnimating = true;
                }

                if (progress < duration) {
                    this.animationFrame = requestAnimationFrame(animate);
                } else {
                    this.animatedValue = end;
                    this.hasValueAnimationFinished = true;
                    this.animationFrame = null;
                }
            };

            this.animationFrame = requestAnimationFrame(animate);
        }
    }
};
</script>

<style scoped>
a {
    display: flex;
    padding: 36px 24px;
    flex-direction: column;
    align-items: center;
    flex: 1 0 0;
    border-radius: var(--border-radius-xl);
    background: var(--brand-bg-1);
    box-shadow: var(--shadow-card-sm);
    text-decoration: none;
    color: inherit;
    cursor: pointer;
    transition: var(--transition-bounce);
    box-shadow: 0 0 0 0px var(--brand-bg-1);
    cursor: default;
}

a:hover {
    box-shadow: 0 0 0 4px var(--brand-bg-1);
    cursor: pointer;
}

a:hover h2 {
    text-decoration: underline;
}

h2 {
    color: var(--brand-color-1);
    opacity: 0;
    transform: translateY(20px);
    transition: opacity 0.5s ease, transform 0.5s ease;
}

/* h2 now only becomes visible when animatedValue >= 1 */
h2.visible {
    opacity: 1;
    transform: translateY(0);
}

.stat-value {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
    align-items: center;
    width: 100%;
    min-width: 0;
}

.stat-value h2 {
    grid-column: 2;
}

.change-marker {
    display: inline-block;
    grid-column: 3;
    justify-self: start;
    position: relative;
    margin-left: 8px;
    font-size: 0.875rem;
    font-weight: 700;
    line-height: 1;
    white-space: nowrap;
    cursor: help;
    outline: none;
    animation: change-marker-enter 280ms ease forwards;
}

@keyframes change-marker-enter {
    from {
        opacity: 0;
        transform: translateX(-8px);
    }

    to {
        opacity: 1;
        transform: translateX(0);
    }
}

.change-marker--up {
    color: #1f8a4c;
}

.change-marker--down {
    color: #ae1e25;
}

.change-tooltip {
    position: absolute;
    left: 50%;
    bottom: calc(100% + 10px);
    z-index: 2;
    width: max-content;
    max-width: min(220px, 70vw);
    padding: 8px 10px;
    border-radius: var(--border-radius-sm, 4px);
    background: var(--brand-text-dark, #2b2621);
    color: var(--brand-text-light, #fff);
    font-size: 0.75rem;
    font-weight: 600;
    line-height: 1.25;
    text-align: center;
    white-space: normal;
    box-shadow: var(--shadow-card-sm);
    opacity: 0;
    pointer-events: none;
    transform: translateX(-50%) translateY(4px);
    transition: opacity 0.16s ease, transform 0.16s ease;
}

.change-tooltip::after {
    content: "";
    position: absolute;
    top: 100%;
    left: 50%;
    border: 5px solid transparent;
    border-top-color: var(--brand-text-dark, #2b2621);
    transform: translateX(-50%);
}

.change-marker:hover .change-tooltip,
.change-marker:focus-visible .change-tooltip {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
}

@media (max-width: 860px) {
    a {
        width: 100%;
        padding: 24px;
    }
}
</style>
